import React, {useEffect, useState} from 'react';
import {TabMenuItem, TabsMenu, TabsSkinEnum} from '@priority-software/priority-style-react';
import {TabIds} from '../../chartConsts';
import styles from '../styles.module.scss';
import {IChartConfig} from '../../types';
import {reportGeneratorsStore} from '../../stores/ReportGeneratorsStore';
import {observer} from 'mobx-react-lite';
import {TabTracker} from './ChartCreationParts/TabTracker';
import {shouldShowFeature, featuresIDs} from '../../../FeaturePopover/constants/map';

interface ChartTabsProps {
    currentChartTab: string;
    setCurrentChartTab: (tabId: string) => void;
    chartInfo: IChartConfig;
}

const ChartTabs: React.FC<ChartTabsProps> = observer(
    ({currentChartTab, setCurrentChartTab, chartInfo}) => {
        const [tabCompletion, setTabCompletion] = useState<boolean[]>([false, false, false]);
        const [mounted, setMounted] = useState<boolean>(false);
        const [showFeaturePopover, setShowFeaturePopover] = useState<boolean>(false);
        const {currentReport, cleanupChangedColumns, isBubble, checkAxesCompleted, setChartReady} =
            reportGeneratorsStore?.currentScreen;
        const forceUpdate = useForceUpdate();

        const tabIds = Object.values(TabIds);
        const shouldShowFeaturePopover = shouldShowFeature(featuresIDs.chartProcessWizard);

        const handleTabClick = (tabId: string) => {
            const currentTab = +currentChartTab;
            const tabIncrementCompleted = +tabId === currentTab + 1 && tabCompletion[currentTab];
            const tabNotGreaterThanCurrent = +tabId <= currentTab;
            const previousTabCompleted = tabCompletion[+tabId - 1];
            if (tabIncrementCompleted || tabNotGreaterThanCurrent || previousTabCompleted) {
                setCurrentChartTab(tabId);
            }
        };

        const handleCleanupColumns = () => {
            let deletedCount = cleanupChangedColumns(chartInfo, currentReport.columns);
            if (isBubble && !!chartInfo.bubbleConfig) {
                deletedCount += cleanupChangedColumns(
                    chartInfo.bubbleConfig,
                    currentReport.columns
                );
            }
            if (deletedCount > 0) {
                setTabCompletion(prev => [prev[0], prev[1], false]);
            }
        };

        useEffect(() => {
            const chartAxesCompleted = checkAxesCompleted(chartInfo);

            const isChartTypeTabCompleted = currentChartTab === '0' && chartInfo.type;
            const isDataTabCompleted = currentChartTab === '1' && currentReport?.columns.length > 0;
            const isAxesTabCompleted = currentChartTab === '2' && chartAxesCompleted;

            if (!chartAxesCompleted) {
                setTabCompletion(prev => [prev[0], prev[1], false]);
                setChartReady(false);
            }

            if (isChartTypeTabCompleted || isDataTabCompleted || isAxesTabCompleted) {
                if (currentReport?.columns.length > 0 && chartAxesCompleted) {
                    setTabCompletion([true, true, true]);
                } else {
                    setTabCompletion(prevTabCompletion => {
                        const updatedCompletion = prevTabCompletion.map(
                            (tab, idx) => idx <= +currentChartTab || !!tab
                        );
                        return updatedCompletion;
                    });
                }
            }
            if (
                shouldShowFeaturePopover &&
                currentReport.isNew &&
                isChartTypeTabCompleted &&
                !isDataTabCompleted
            ) {
                setShowFeaturePopover(true);
            }
        }, [
            chartInfo,
            currentChartTab,
            currentReport,
            currentReport.columns,
            currentReport.columns.length
        ]);

        useEffect(() => {
            if (currentReport?.columns.length === 0) {
                setTabCompletion(prev => [prev[0], false, false]);
                forceUpdate();
            }
            if (mounted) {
                handleCleanupColumns();
            }
        }, [currentReport.columns.length]);

        useEffect(() => setMounted(true), []);

        return (
            <TabsMenu skin={TabsSkinEnum.folder}>
                {tabIds.map((tabId, idx) => {
                    const tabIndex = idx.toString();
                    const showIncomplete = currentChartTab === tabIndex && !tabCompletion[idx];
                    const showComplete = tabCompletion[idx] || false;
                    const unlockTab = idx <= +currentChartTab || tabCompletion[idx - 1];
                    return (
                        <TabMenuItem
                            componentClasses={unlockTab ? null : styles.tabMenuLocked}
                            key={tabId}
                            id={tabIndex}
                            text={
                                <TabTracker
                                    showComplete={showComplete}
                                    showIncomplete={showIncomplete}
                                    tabId={tabId}
                                    isLast={idx === tabIds.length - 1}
                                    showFeaturePopover={showFeaturePopover}
                                />
                            }
                            isActive={currentChartTab === tabIndex}
                            onTabClick={() => handleTabClick(tabIndex)}
                        />
                    );
                })}
            </TabsMenu>
        );
    }
);

const useForceUpdate = () => {
    const [, forceUpdate] = useState<any>({});
    return () => forceUpdate({});
};

export default ChartTabs;
